import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Progress, Tabs, Typography, Divider, List, Tag, Tooltip, Spin, Alert, Table, Badge, Avatar } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, InfoCircleOutlined, LineChartOutlined, BarChartOutlined, PieChartOutlined, DollarOutlined, TrophyOutlined, FireOutlined, RiseOutlined, FallOutlined, CheckCircleOutlined, WarningOutlined, TeamOutlined } from '@ant-design/icons';
import { Analytics } from './Analytics';
import { StockAnalytics, StockHolding, PerformanceAttribution } from './types';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  Area,
  RadialBarChart,
  RadialBar
} from 'recharts';
import type { SizeType } from 'antd/es/config-provider/SizeContext';
import { motion } from 'framer-motion';

const { TabPane } = Tabs;
const { Title, Text, Paragraph } = Typography;

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const staggerChildren = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57'];

// Mock data - Replace with actual API call
const fetchAnalytics = async (holdings: StockHolding[]): Promise<StockAnalytics> => {
  // Simulated API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  const totalValue = holdings.reduce((sum, stock) => sum + stock.currentValue, 0);
  const totalInvestment = holdings.reduce((sum, stock) => sum + stock.totalInvestment, 0);
  
  const sectorExposure = holdings.reduce((acc, stock) => {
    acc[stock.sector] = (acc[stock.sector] || 0) + stock.currentValue;
    return acc;
  }, {} as Record<string, number>);

  const marketCapDistribution = holdings.reduce((acc, stock) => {
    acc[stock.marketCap] = (acc[stock.marketCap] || 0) + stock.currentValue;
    return acc;
  }, {} as Record<string, number>);

  return {
    sectorDistribution: Object.entries(sectorExposure).map(([sector, value]) => ({
      sector,
      percentage: (value / totalValue) * 100,
      value
    })),
    marketCapDistribution: Object.entries(marketCapDistribution).map(([cap, value]) => ({
      category: cap,
      percentage: (value / totalValue) * 100,
      value
    })),
    performanceMetrics: {
      sharpeRatio: 1.2,
      beta: 0.95,
      alpha: 2.5,
      rSquared: 0.85,
      trackingError: 3.2,
      informationRatio: 1.8,
      xirr: 15.8,
      absoluteReturn: 12.5,
      cagr: 14.2,
      totalInvestment: 1000000,
      currentValue: 1125000,
      totalProfitLoss: 125000
    },
    riskMetrics: {
      beta: 1.2,
      concentration: 35.5,
      top10Holdings: 75.8,
      volatility: 15.5,
      maxDrawdown: -12.3,
      var95: -8.5,
      var99: -12.8,
      sortinoRatio: 1.2,
      treynorRatio: 0.9,
    },
    correlationMatrix: {
      'RELIANCE': {
        'RELIANCE': 1.0,
        'TCS': 0.3,
        'HDFCBANK': 0.4,
        'INFY': 0.2,
        'ICICIBANK': 0.5,
      },
      'TCS': {
        'RELIANCE': 0.3,
        'TCS': 1.0,
        'HDFCBANK': 0.2,
        'INFY': 0.8,
        'ICICIBANK': 0.3,
      },
      'HDFCBANK': {
        'RELIANCE': 0.4,
        'TCS': 0.2,
        'HDFCBANK': 1.0,
        'INFY': 0.1,
        'ICICIBANK': 0.7,
      },
      'INFY': {
        'RELIANCE': 0.2,
        'TCS': 0.8,
        'HDFCBANK': 0.1,
        'INFY': 1.0,
        'ICICIBANK': 0.2,
      },
      'ICICIBANK': {
        'RELIANCE': 0.5,
        'TCS': 0.3,
        'HDFCBANK': 0.7,
        'INFY': 0.2,
        'ICICIBANK': 1.0,
      },
    },
    taxAnalysis: {
      shortTermGains: 25000,
      longTermGains: 75000,
      taxLiability: 15000,
      taxSavingOpportunities: [
        {
          type: 'ELSS Mutual Fund',
          amount: 150000,
          description: 'Invest in ELSS to save tax under Section 80C',
        },
        {
          type: 'NPS Contribution',
          amount: 50000,
          description: 'Additional tax benefit under Section 80CCD(1B)',
        },
        {
          type: 'Health Insurance',
          amount: 25000,
          description: 'Tax benefit under Section 80D',
        },
      ],
    },
    benchmarkComparison: {
      nifty50: {
        value: 18.5,
        difference: 5.8,
      },
      niftyMidcap: {
        value: 22.4,
        difference: 3.2,
      },
      niftySmallcap: {
        value: 24.8,
        difference: 4.6,
      },
      sensex: {
        value: 17.8,
        difference: 6.5,
      },
    },
    fiiDiiActivity: {
      fii: {
        buy: 12500,
        sell: 9800,
        net: 2700,
      },
      dii: {
        buy: 8900,
        sell: 7600,
        net: 1300,
      },
    },
    marketInsights: {
      date: new Date().toISOString(),
      marketBreadth: {
        advances: 1250,
        declines: 850,
        unchanged: 150,
      },
      sectorPerformance: [
        { sector: 'Technology', change: 3.2 },
        { sector: 'Finance', change: 1.8 },
        { sector: 'Healthcare', change: -0.5 },
        { sector: 'Consumer', change: 2.1 },
        { sector: 'Energy', change: -1.2 },
        { sector: 'Auto', change: 0.7 },
        { sector: 'Real Estate', change: 4.5 },
        { sector: 'Metals', change: -2.3 },
      ],
    },
    recommendations: [
      {
        type: 'BUY',
        symbol: 'HDFCBANK',
        reason: 'Strong fundamentals with improving asset quality and robust credit growth',
        targetPrice: 1850,
        stopLoss: 1650,
        timeframe: '3-6 months',
      },
      {
        type: 'SELL',
        symbol: 'YESBANK',
        reason: 'Weak asset quality and declining profitability metrics',
        targetPrice: 15,
        stopLoss: 22,
        timeframe: '1-3 months',
      },
      {
        type: 'HOLD',
        symbol: 'TCS',
        reason: 'Stable performance but rich valuations limit upside in near term',
        targetPrice: 3800,
        stopLoss: 3400,
        timeframe: '6-12 months',
      },
    ],
    crossSellingOpportunities: [
      {
        type: 'MUTUAL_FUND',
        name: 'Axis Bluechip Fund Direct Growth',
        description: 'Consistently performing large-cap fund with strong track record',
        expectedReturn: 12,
        riskLevel: 'MEDIUM',
        link: '/mutual-funds/axis-bluechip-fund',
      },
      {
        type: 'FIXED_DEPOSIT',
        name: 'HDFC Bank Fixed Deposit',
        description: 'Secure investment with guaranteed returns',
        expectedReturn: 6.5,
        riskLevel: 'LOW',
        link: '/fixed-deposits/hdfc-bank',
      },
      {
        type: 'GOLD',
        name: 'Sovereign Gold Bonds',
        description: 'Government-backed gold investment with additional interest',
        expectedReturn: 8,
        riskLevel: 'LOW',
        link: '/gold/sovereign-gold-bonds',
      },
      {
        type: 'INSURANCE',
        name: 'HDFC Life Term Insurance',
        description: 'Comprehensive life coverage with tax benefits',
        expectedReturn: 0,
        riskLevel: 'LOW',
        link: '/insurance/hdfc-life-term',
      },
      {
        type: 'DEBT_FUND',
        name: 'ICICI Prudential Liquid Fund',
        description: 'Low-risk debt fund for short-term parking of funds',
        expectedReturn: 5.2,
        riskLevel: 'LOW',
        link: '/debt-funds/icici-prudential-liquid',
      },
    ],
    historicalPerformance: [
      { month: 'Jan', portfolio: 18.5, nifty50: 15.2, sensex: 14.8 },
      { month: 'Feb', portfolio: 20.2, nifty50: 16.5, sensex: 15.1 },
      { month: 'Mar', portfolio: 22.8, nifty50: 17.9, sensex: 16.5 },
      { month: 'Apr', portfolio: 24.1, nifty50: 18.2, sensex: 17.8 },
      { month: 'May', portfolio: 25.3, nifty50: 19.5, sensex: 18.7 },
      { month: 'Jun', portfolio: 26.2, nifty50: 20.1, sensex: 19.3 },
    ],
    portfolioTurnover: {
      monthly: 5.2,
      quarterly: 12.8,
      yearly: 45.5,
    },
    dividendAnalysis: {
      totalDividend: 12500,
      dividendYield: 2.5,
      topDividendStocks: [
        { symbol: 'HDFCBANK', amount: 2500, yield: 3.2 },
        { symbol: 'INFY', amount: 1800, yield: 2.8 },
        { symbol: 'TCS', amount: 1500, yield: 2.5 },
        { symbol: 'ICICIBANK', amount: 1200, yield: 2.1 },
        { symbol: 'RELIANCE', amount: 1000, yield: 1.8 },
      ],
    },
    topContributors: [
      {
        name: 'HDFC Bank',
        value: 250000,
        return_pct: 18.5,
        contribution: 3.2,
        key: '1'
      },
      {
        name: 'Infosys',
        value: 180000,
        return_pct: 15.2,
        contribution: 2.8,
        key: '2'
      },
      {
        name: 'TCS',
        value: 165000,
        return_pct: 12.8,
        contribution: 2.5,
        key: '3'
      },
      {
        name: 'Reliance Industries',
        value: 145000,
        return_pct: 11.5,
        contribution: 2.1,
        key: '4'
      },
      {
        name: 'ITC',
        value: 125000,
        return_pct: 10.2,
        contribution: 1.8,
        key: '5'
      }
    ],
    bottomContributors: [
      {
        name: 'Yes Bank',
        value: 45000,
        return_pct: -15.8,
        contribution: -2.5,
        key: '1'
      },
      {
        name: 'Zomato',
        value: 55000,
        return_pct: -12.5,
        contribution: -2.1,
        key: '2'
      },
      {
        name: 'Paytm',
        value: 65000,
        return_pct: -10.2,
        contribution: -1.8,
        key: '3'
      },
      {
        name: 'IndusInd Bank',
        value: 75000,
        return_pct: -8.5,
        contribution: -1.5,
        key: '4'
      },
      {
        name: 'Bharti Airtel',
        value: 85000,
        return_pct: -5.2,
        contribution: -1.2,
        key: '5'
      }
    ],
    peerAnalysis: {
      portfolioAge: 18,
      portfolioToIncome: 85.5,
      ranking: 1250,
      totalUsers: 5000,
      allocationComparison: {
        largeCap: 60,
        midCap: 25,
        smallCap: 15,
      },
      performanceComparison: {
        percentile: 75,
        averageReturn: 15.5,
        userReturn: 22.5,
      },
    },
  };
};

interface Props {
  isMobile?: boolean;
  isTablet?: boolean;
  holdings: StockHolding[];
  performanceAttribution?: PerformanceAttribution;
}

const AnalyticsContainer: React.FC<Props> = ({ isMobile, isTablet, holdings, performanceAttribution: externalPerformanceAttribution }) => {
  const [analytics, setAnalytics] = useState<StockAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setLoading(true);
        const data = await fetchAnalytics(holdings);
        setAnalytics(data);
      } catch (err) {
        setError('Failed to load analytics data');
        console.error('Error loading analytics:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [holdings]);

  const getColSpan = () => {
    if (isMobile) return 24;
    if (isTablet) return 12;
    return 8;
  };

  const cardStyle = {
    margin: isMobile ? '8px 0' : '16px',
    height: isMobile ? 'auto' : '300px',
  };

  // Calculate portfolio metrics
  const calculatePortfolioMetrics = () => {
    const totalValue = holdings.reduce((sum, stock) => sum + stock.currentValue, 0);
    const totalInvestment = holdings.reduce((sum, stock) => sum + stock.totalInvestment, 0);
    
    const sectorExposure = holdings.reduce((acc, stock) => {
      acc[stock.sector] = (acc[stock.sector] || 0) + stock.currentValue;
      return acc;
    }, {} as Record<string, number>);

    const marketCapDistribution = holdings.reduce((acc, stock) => {
      acc[stock.marketCap] = (acc[stock.marketCap] || 0) + stock.currentValue;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalValue,
      totalInvestment,
      overallReturn: ((totalValue - totalInvestment) / totalInvestment) * 100,
      sectorExposure,
      marketCapDistribution
    };
  };

  const metrics = calculatePortfolioMetrics();

  // Mock index data
  const indexComparison = {
    nifty50: {
      return: 18.5,
      beta: 0.95,
      correlation: 0.85,
      alpha: 5.2
    },
    sensex: {
      return: 17.8,
      beta: 0.92,
      correlation: 0.82,
      alpha: 4.8
    }
  };

  if (loading) {
    return (
      <Card style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <Paragraph style={{ marginTop: '20px' }}>Loading portfolio analytics...</Paragraph>
      </Card>
    );
  }

  if (error || !analytics) {
    return (
      <Card>
        <div style={{ color: 'red' }}>{error || 'Failed to load analytics data'}</div>
      </Card>
    );
  }

  const renderPerformanceAttribution = () => {
    if (!externalPerformanceAttribution) {
      return (
        <motion.div {...fadeInUp}>
          <Card>
            <Alert
              message="No Performance Attribution Data"
              description="Performance attribution data is not available."
              type="info"
            />
          </Card>
        </motion.div>
      );
    }
    
    const contributorsColumns = [
      {
        title: 'Stock',
        dataIndex: 'name',
        key: 'name',
        render: (text: string) => (
          <motion.div whileHover={{ scale: 1.05 }} style={{ cursor: 'pointer' }}>
            <Avatar style={{ backgroundColor: '#1890ff', marginRight: 8 }}>{text[0]}</Avatar>
            {text}
          </motion.div>
        ),
      },
      {
        title: 'Current Value',
        dataIndex: 'value',
        key: 'value',
        render: (value: number) => (
          <motion.div whileHover={{ scale: 1.05 }}>
            <Badge status="processing" text={`₹${value.toLocaleString('en-IN')}`} />
          </motion.div>
        ),
      },
      {
        title: 'Return %',
        dataIndex: 'return_pct',
        key: 'return_pct',
        render: (value: number) => (
          <motion.div whileHover={{ scale: 1.05 }}>
            <Tag color={value >= 0 ? 'success' : 'error'} icon={value >= 0 ? <RiseOutlined /> : <FallOutlined />}>
              {value.toFixed(2)}%
            </Tag>
          </motion.div>
        ),
      },
      {
        title: 'Contribution',
        dataIndex: 'contribution',
        key: 'contribution',
        render: (value: number) => (
          <motion.div whileHover={{ scale: 1.05 }}>
            <Progress
              percent={Math.abs(value)}
              size="small"
              status={value >= 0 ? 'success' : 'exception'}
              format={() => `${value.toFixed(2)}%`}
            />
          </motion.div>
        ),
      },
    ];

    return (
      <motion.div variants={staggerChildren} initial="initial" animate="animate">
        <motion.div variants={fadeInUp}>
          <Card title={
            <span>
              <TrophyOutlined style={{ marginRight: 8, color: '#ffd700' }} />
              Performance Attribution
            </span>
          }>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card title="Overall Performance" className="performance-card">
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <motion.div whileHover={{ scale: 1.05 }}>
                        <Statistic
                          title="Total Value"
                          value={externalPerformanceAttribution.overall.totalValue}
                          precision={2}
                          prefix="₹"
                          valueStyle={{ fontSize: '24px', color: '#1890ff' }}
                        />
                      </motion.div>
                    </Col>
                    <Col span={8}>
                      <motion.div whileHover={{ scale: 1.05 }}>
                        <Statistic
                          title="Total Investment"
                          value={externalPerformanceAttribution.overall.totalInvestment}
                          precision={2}
                          prefix="₹"
                          valueStyle={{ fontSize: '24px', color: '#52c41a' }}
                        />
                      </motion.div>
                    </Col>
                    <Col span={8}>
                      <motion.div whileHover={{ scale: 1.05 }}>
                        <Statistic
                          title="Overall Return"
                          value={externalPerformanceAttribution.overall.overallReturn}
                          precision={2}
                          suffix="%"
                          valueStyle={{ 
                            fontSize: '24px',
                            color: externalPerformanceAttribution.overall.overallReturn >= 0 ? '#3f8600' : '#cf1322'
                          }}
                          prefix={externalPerformanceAttribution.overall.overallReturn >= 0 ? <RiseOutlined /> : <FallOutlined />}
                        />
                      </motion.div>
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col span={24}>
                <motion.div variants={fadeInUp}>
                  <Card 
                    title={
                      <span>
                        <FireOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />
                        Top Contributors
                      </span>
                    }
                    className="contributors-card"
                  >
                    <Table
                      columns={contributorsColumns}
                      dataSource={analytics.topContributors}
                      pagination={false}
                      size="middle"
                      className="contributors-table"
                    />
                  </Card>
                </motion.div>
              </Col>
              <Col span={24}>
                <motion.div variants={fadeInUp}>
                  <Card 
                    title={
                      <span>
                        <WarningOutlined style={{ marginRight: 8, color: '#faad14' }} />
                        Top Detractors
                      </span>
                    }
                    className="detractors-card"
                  >
                    <Table
                      columns={contributorsColumns}
                      dataSource={analytics.bottomContributors}
                      pagination={false}
                      size="middle"
                      className="detractors-table"
                    />
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </Card>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Tabs 
        defaultActiveKey="overview" 
        activeKey={activeTab} 
        onChange={setActiveTab}
        className="analytics-tabs"
      >
        <TabPane 
          tab={
            <span>
              <LineChartOutlined /> Portfolio Overview
            </span>
          } 
          key="overview"
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card title="Portfolio Performance" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col span={getColSpan()}>
                    <Statistic
                      title="Overall Return"
                      value={metrics.overallReturn}
                      precision={2}
                      valueStyle={{ color: metrics.overallReturn >= 0 ? '#3f8600' : '#cf1322' }}
                      prefix={metrics.overallReturn >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      suffix="%"
                    />
                    <Progress
                      percent={Math.min(100, Math.abs(metrics.overallReturn))}
                      status={metrics.overallReturn >= 0 ? 'success' : 'exception'}
                      showInfo={false}
                    />
                    <Paragraph type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                      Total portfolio value: ₹{metrics.totalValue.toLocaleString()}
                    </Paragraph>
                  </Col>
                  
                  <Col span={getColSpan()}>
                    <Statistic
                      title="vs NIFTY 50"
                      value={metrics.overallReturn - indexComparison.nifty50.return}
                      precision={2}
                      valueStyle={{ 
                        color: metrics.overallReturn >= indexComparison.nifty50.return ? '#3f8600' : '#cf1322' 
                      }}
                      suffix="%"
                    />
                    <Progress
                      percent={Math.min(100, Math.abs(metrics.overallReturn - indexComparison.nifty50.return))}
                      status={metrics.overallReturn >= indexComparison.nifty50.return ? 'success' : 'exception'}
                      showInfo={false}
                    />
                    <Paragraph type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                      NIFTY 50 return: {indexComparison.nifty50.return}%
                    </Paragraph>
                  </Col>

                  <Col span={getColSpan()}>
                    <Statistic
                      title="Portfolio Beta"
                      value={indexComparison.nifty50.beta}
                      precision={2}
                    />
                    <Progress
                      percent={indexComparison.nifty50.beta * 50}
                      status={indexComparison.nifty50.beta > 1.2 ? 'exception' : indexComparison.nifty50.beta < 0.8 ? 'normal' : 'success'}
                      showInfo={false}
                    />
                    <Paragraph type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                      {indexComparison.nifty50.beta > 1.2 
                        ? 'Higher volatility than market' 
                        : indexComparison.nifty50.beta < 0.8 
                          ? 'Lower volatility than market' 
                          : 'Similar volatility to market'}
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
            
            <Col span={24}>
              <Card title="Historical Performance" style={{ marginBottom: 16 }}>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={analytics.historicalPerformance}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="portfolio" stroke="#8884d8" name="Your Portfolio" />
                    <Line type="monotone" dataKey="nifty50" stroke="#82ca9d" name="NIFTY 50" />
                    <Line type="monotone" dataKey="sensex" stroke="#ffc658" name="SENSEX" />
                  </LineChart>
                </ResponsiveContainer>
                <Paragraph type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                  Your portfolio has outperformed the market indices over the last 6 months.
                </Paragraph>
              </Card>
            </Col>
            
            <Col span={getColSpan()}>
              <Card title="Risk Metrics" style={cardStyle}>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    <Statistic
                      title="Alpha"
                      value={indexComparison.nifty50.alpha}
                      precision={2}
                      suffix="%"
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Correlation"
                      value={indexComparison.nifty50.correlation}
                      precision={2}
                    />
                  </Col>
                  <Col span={24}>
                    <Divider style={{ margin: '8px 0' }} />
                    <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                      <InfoCircleOutlined /> Alpha measures excess return compared to the market. Higher is better.
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
            
            <Col span={getColSpan()}>
              <Card title="Portfolio Turnover" style={cardStyle}>
                <Row gutter={[8, 8]}>
                  <Col span={8}>
                    <Statistic
                      title="Monthly"
                      value={analytics.portfolioTurnover.monthly}
                      precision={1}
                      suffix="%"
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Quarterly"
                      value={analytics.portfolioTurnover.quarterly}
                      precision={1}
                      suffix="%"
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Yearly"
                      value={analytics.portfolioTurnover.yearly}
                      precision={1}
                      suffix="%"
                    />
                  </Col>
                  <Col span={24}>
                    <Divider style={{ margin: '8px 0' }} />
                    <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                      <InfoCircleOutlined /> Portfolio turnover measures how frequently stocks are bought and sold.
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
            
            <Col span={getColSpan()}>
              <Card title="Dividend Analysis" style={cardStyle}>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    <Statistic
                      title="Total Dividend"
                      value={analytics.dividendAnalysis.totalDividend}
                      precision={0}
                      prefix="₹"
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Dividend Yield"
                      value={analytics.dividendAnalysis.dividendYield}
                      precision={2}
                      suffix="%"
                    />
                  </Col>
                  <Col span={24}>
                    <Divider style={{ margin: '8px 0' }} />
                    <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                      <InfoCircleOutlined /> Dividend yield is the annual dividend payment as a percentage of your investment.
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <PieChartOutlined /> Sector Analysis
            </span>
          } 
          key="sector"
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card title="Sector Distribution" style={{ marginBottom: 16 }}>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={Object.entries(metrics.sectorExposure).map(([sector, value]) => ({
                        sector,
                        value,
                        percentage: (value / metrics.totalValue) * 100
                      }))}
                      dataKey="value"
                      nameKey="sector"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    >
                      {Object.entries(metrics.sectorExposure).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value, name, props) => [
                      `₹${Number(value).toLocaleString()}`,
                      props.payload.sector
                    ]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                <Paragraph type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                  Your portfolio is most concentrated in {Object.entries(metrics.sectorExposure)
                    .sort((a, b) => b[1] - a[1])[0][0]} sector with {(Object.entries(metrics.sectorExposure)
                    .sort((a, b) => b[1] - a[1])[0][1] / metrics.totalValue * 100).toFixed(1)}% allocation.
                </Paragraph>
              </Card>
            </Col>
            
            {Object.entries(metrics.sectorExposure).map(([sector, value]) => (
              <Col span={getColSpan()} key={sector}>
                <Card title={sector} style={cardStyle}>
                  <Statistic
                    title="Exposure"
                    value={(value / metrics.totalValue) * 100}
                    precision={1}
                    suffix="%"
                  />
                  <Progress
                    percent={(value / metrics.totalValue) * 100}
                    showInfo={false}
                  />
                  <Divider style={{ margin: '8px 0' }} />
                  <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                    {sector} stocks in your portfolio: {holdings.filter(stock => stock.sector === sector).length}
                  </Paragraph>
                </Card>
              </Col>
            ))}
            
            <Col span={24}>
              <Card title="Sector Performance" style={{ marginBottom: 16 }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={analytics.marketInsights.sectorPerformance}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="sector" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="change" name="Performance" fill="#8884d8">
                      {analytics.marketInsights.sectorPerformance.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.change >= 0 ? '#3f8600' : '#cf1322'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <Paragraph type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                  Top performing sector: {analytics.marketInsights.sectorPerformance
                    .sort((a, b) => b.change - a.change)[0].sector} ({analytics.marketInsights.sectorPerformance
                    .sort((a, b) => b.change - a.change)[0].change}%)
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <BarChartOutlined /> Market Cap Analysis
            </span>
          } 
          key="marketCap"
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card title="Market Cap Distribution" style={{ marginBottom: 16 }}>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={Object.entries(metrics.marketCapDistribution).map(([cap, value]) => ({
                        category: cap,
                        value,
                        percentage: (value / metrics.totalValue) * 100
                      }))}
                      dataKey="value"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    >
                      {Object.entries(metrics.marketCapDistribution).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value, name, props) => [
                      `₹${Number(value).toLocaleString()}`,
                      props.payload.category
                    ]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                <Paragraph type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                  Your portfolio is most concentrated in {Object.entries(metrics.marketCapDistribution)
                    .sort((a, b) => b[1] - a[1])[0][0]} stocks with {(Object.entries(metrics.marketCapDistribution)
                    .sort((a, b) => b[1] - a[1])[0][1] / metrics.totalValue * 100).toFixed(1)}% allocation.
                </Paragraph>
              </Card>
            </Col>
            
            {Object.entries(metrics.marketCapDistribution).map(([cap, value]) => (
              <Col span={getColSpan()} key={cap}>
                <Card title={cap} style={cardStyle}>
                  <Statistic
                    title="Allocation"
                    value={(value / metrics.totalValue) * 100}
                    precision={1}
                    suffix="%"
                  />
                  <Progress
                    percent={(value / metrics.totalValue) * 100}
                    showInfo={false}
                  />
                  <Divider style={{ margin: '8px 0' }} />
                  <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                    {cap} stocks in your portfolio: {holdings.filter(stock => stock.marketCap === cap).length}
                  </Paragraph>
                </Card>
              </Col>
            ))}
            
            <Col span={24}>
              <Card title="Market Cap Performance" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title="NIFTY 50 (Large Cap)"
                        value={analytics.benchmarkComparison.nifty50.value}
                        precision={1}
                        suffix="%"
                        valueStyle={{ 
                          color: analytics.benchmarkComparison.nifty50.difference >= 0 ? '#3f8600' : '#cf1322' 
                        }}
                        prefix={analytics.benchmarkComparison.nifty50.difference >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      />
                      <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                        Difference: {analytics.benchmarkComparison.nifty50.difference}%
                      </Paragraph>
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title="NIFTY Midcap"
                        value={analytics.benchmarkComparison.niftyMidcap.value}
                        precision={1}
                        suffix="%"
                        valueStyle={{ 
                          color: analytics.benchmarkComparison.niftyMidcap.difference >= 0 ? '#3f8600' : '#cf1322' 
                        }}
                        prefix={analytics.benchmarkComparison.niftyMidcap.difference >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      />
                      <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                        Difference: {analytics.benchmarkComparison.niftyMidcap.difference}%
                      </Paragraph>
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title="NIFTY Smallcap"
                        value={analytics.benchmarkComparison.niftySmallcap.value}
                        precision={1}
                        suffix="%"
                        valueStyle={{ 
                          color: analytics.benchmarkComparison.niftySmallcap.difference >= 0 ? '#3f8600' : '#cf1322' 
                        }}
                        prefix={analytics.benchmarkComparison.niftySmallcap.difference >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      />
                      <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                        Difference: {analytics.benchmarkComparison.niftySmallcap.difference}%
                      </Paragraph>
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title="SENSEX"
                        value={analytics.benchmarkComparison.sensex.value}
                        precision={1}
                        suffix="%"
                        valueStyle={{ 
                          color: analytics.benchmarkComparison.sensex.difference >= 0 ? '#3f8600' : '#cf1322' 
                        }}
                        prefix={analytics.benchmarkComparison.sensex.difference >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      />
                      <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                        Difference: {analytics.benchmarkComparison.sensex.difference}%
                      </Paragraph>
                    </Card>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <LineChartOutlined /> Performance Attribution
            </span>
          } 
          key="attribution"
        >
          {renderPerformanceAttribution()}
        </TabPane>
        
        <TabPane 
          tab={
            <span>
              <InfoCircleOutlined /> Market Insights
            </span>
          } 
          key="insights"
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card title="Market Breadth" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Statistic
                      title="Advances"
                      value={analytics.marketInsights.marketBreadth.advances}
                      valueStyle={{ color: '#3f8600' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Declines"
                      value={analytics.marketInsights.marketBreadth.declines}
                      valueStyle={{ color: '#cf1322' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Unchanged"
                      value={analytics.marketInsights.marketBreadth.unchanged}
                      valueStyle={{ color: '#8c8c8c' }}
                    />
                  </Col>
                </Row>
                <Divider style={{ margin: '16px 0' }} />
                <Paragraph>
                  Market breadth is a technique used in technical analysis that attempts to gauge the direction of the overall market by analyzing the number of companies advancing relative to the number declining.
                </Paragraph>
                <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                  Last updated: {new Date(analytics.marketInsights.date).toLocaleDateString()}
                </Paragraph>
              </Card>
            </Col>
            
            <Col span={24}>
              <Card title="FII/DII Activity" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card title="Foreign Institutional Investors (FII)">
                      <Row gutter={[8, 8]}>
                        <Col span={8}>
                          <Statistic
                            title="Buy"
                            value={analytics.fiiDiiActivity.fii.buy}
                            valueStyle={{ color: '#3f8600' }}
                            prefix="₹"
                          />
                        </Col>
                        <Col span={8}>
                          <Statistic
                            title="Sell"
                            value={analytics.fiiDiiActivity.fii.sell}
                            valueStyle={{ color: '#cf1322' }}
                            prefix="₹"
                          />
                        </Col>
                        <Col span={8}>
                          <Statistic
                            title="Net"
                            value={analytics.fiiDiiActivity.fii.net}
                            valueStyle={{ 
                              color: analytics.fiiDiiActivity.fii.net >= 0 ? '#3f8600' : '#cf1322' 
                            }}
                            prefix="₹"
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title="Domestic Institutional Investors (DII)">
                      <Row gutter={[8, 8]}>
                        <Col span={8}>
                          <Statistic
                            title="Buy"
                            value={analytics.fiiDiiActivity.dii.buy}
                            valueStyle={{ color: '#3f8600' }}
                            prefix="₹"
                          />
                        </Col>
                        <Col span={8}>
                          <Statistic
                            title="Sell"
                            value={analytics.fiiDiiActivity.dii.sell}
                            valueStyle={{ color: '#cf1322' }}
                            prefix="₹"
                          />
                        </Col>
                        <Col span={8}>
                          <Statistic
                            title="Net"
                            value={analytics.fiiDiiActivity.dii.net}
                            valueStyle={{ 
                              color: analytics.fiiDiiActivity.dii.net >= 0 ? '#3f8600' : '#cf1322' 
                            }}
                            prefix="₹"
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>
                <Divider style={{ margin: '16px 0' }} />
                <Paragraph>
                  FII and DII activity provides insights into the investment behavior of institutional investors, which can be a leading indicator of market direction.
                </Paragraph>
              </Card>
            </Col>
            
            <Col span={24}>
              <Card title="Analyst Recommendations" style={{ marginBottom: 16 }}>
                <List
                  itemLayout="horizontal"
                  dataSource={analytics.recommendations}
                  renderItem={item => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Tag color={
                            item.type === 'BUY' ? 'green' : 
                            item.type === 'SELL' ? 'red' : 'orange'
                          }>
                            {item.type}
                          </Tag>
                        }
                        title={`${item.symbol} - Target: ₹${item.targetPrice} | Stop Loss: ₹${item.stopLoss}`}
                        description={
                          <>
                            <Paragraph>{item.reason}</Paragraph>
                            <Paragraph type="secondary">Timeframe: {item.timeframe}</Paragraph>
                          </>
                        }
                      />
                    </List.Item>
                  )}
                />
                <Divider style={{ margin: '16px 0' }} />
                <Paragraph>
                  These recommendations are based on technical and fundamental analysis of your portfolio stocks.
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </TabPane>
        
        <TabPane 
          tab={
            <span>
              <DollarOutlined /> Tax Analysis
            </span>
          } 
          key="tax"
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card title="Capital Gains Summary" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Statistic
                      title="Short Term Gains"
                      value={analytics.taxAnalysis.shortTermGains}
                      precision={0}
                      prefix="₹"
                    />
                    <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                      Taxable at your income tax slab rate
                    </Paragraph>
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Long Term Gains"
                      value={analytics.taxAnalysis.longTermGains}
                      precision={0}
                      prefix="₹"
                    />
                    <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                      Taxable at 10% (without indexation) or 20% (with indexation)
                    </Paragraph>
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Estimated Tax Liability"
                      value={analytics.taxAnalysis.taxLiability}
                      precision={0}
                      prefix="₹"
                      valueStyle={{ color: '#cf1322' }}
                    />
                  </Col>
                </Row>
                <Divider style={{ margin: '16px 0' }} />
                <Paragraph>
                  Capital gains from equity investments are taxed differently based on the holding period. Short-term gains (less than 1 year) are taxed at your income tax slab rate, while long-term gains (more than 1 year) are taxed at 10% without indexation or 20% with indexation.
                </Paragraph>
              </Card>
            </Col>
            
            <Col span={24}>
              <Card title="Tax Saving Opportunities" style={{ marginBottom: 16 }}>
                <List
                  itemLayout="horizontal"
                  dataSource={analytics.taxAnalysis.taxSavingOpportunities}
                  renderItem={item => (
                    <List.Item>
                      <List.Item.Meta
                        title={item.type}
                        description={
                          <>
                            <Paragraph>{item.description}</Paragraph>
                            <Paragraph type="secondary">Amount: ₹{item.amount.toLocaleString()}</Paragraph>
                          </>
                        }
                      />
                    </List.Item>
                  )}
                />
                <Divider style={{ margin: '16px 0' }} />
                <Paragraph>
                  These tax-saving opportunities can help you reduce your overall tax liability.
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <BarChartOutlined /> Insights
            </span>
          } 
          key="insights"
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <motion.div variants={fadeInUp}>
                <Card title={
                  <span>
                    <LineChartOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                    Portfolio Overview
                  </span>
                }>
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <motion.div whileHover={{ scale: 1.05 }}>
                        <Statistic
                          title="Total Investment"
                          value={analytics.performanceMetrics.totalInvestment}
                          precision={2}
                          prefix="₹"
                          valueStyle={{ fontSize: '24px', color: '#52c41a' }}
                        />
                      </motion.div>
                    </Col>
                    <Col span={8}>
                      <motion.div whileHover={{ scale: 1.05 }}>
                        <Statistic
                          title="Current Value"
                          value={analytics.performanceMetrics.currentValue}
                          precision={2}
                          prefix="₹"
                          valueStyle={{ fontSize: '24px', color: '#1890ff' }}
                        />
                      </motion.div>
                    </Col>
                    <Col span={8}>
                      <motion.div whileHover={{ scale: 1.05 }}>
                        <Statistic
                          title="Total Profit/Loss"
                          value={analytics.performanceMetrics.totalProfitLoss}
                          precision={2}
                          valueStyle={{ 
                            fontSize: '24px',
                            color: analytics.performanceMetrics.totalProfitLoss >= 0 ? '#3f8600' : '#cf1322'
                          }}
                          prefix={analytics.performanceMetrics.totalProfitLoss >= 0 ? <RiseOutlined /> : <FallOutlined />}
                        />
                      </motion.div>
                    </Col>
                  </Row>
                </Card>
              </motion.div>
            </Col>

            <Col span={24}>
              <motion.div variants={fadeInUp}>
                <Card title={
                  <span>
                    <PieChartOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                    Asset Allocation
                  </span>
                }>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Card title="Stocks vs ETFs">
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Stocks', value: 85 },
                                { name: 'ETFs', value: 15 }
                              ]}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label
                            >
                              <Cell fill="#1890ff" />
                              <Cell fill="#722ed1" />
                            </Pie>
                            <RechartsTooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card title="Market Cap Distribution">
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Large Cap', value: 60 },
                                { name: 'Mid Cap', value: 25 },
                                { name: 'Small Cap', value: 15 }
                              ]}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label
                            >
                              <Cell fill="#52c41a" />
                              <Cell fill="#faad14" />
                              <Cell fill="#f5222d" />
                            </Pie>
                            <RechartsTooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </motion.div>
            </Col>

            <Col span={24}>
              <motion.div variants={fadeInUp}>
                <Card title={
                  <span>
                    <WarningOutlined style={{ marginRight: 8, color: '#faad14' }} />
                    Risk Analysis
                  </span>
                }>
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Card>
                        <Statistic
                          title="Portfolio Beta"
                          value={analytics.riskMetrics.beta}
                          precision={2}
                          suffix="vs Nifty 50"
                        />
                        <Progress
                          percent={analytics.riskMetrics.beta * 50}
                          status={analytics.riskMetrics.beta > 1.2 ? 'exception' : 'success'}
                          showInfo={false}
                        />
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card>
                        <Statistic
                          title="Portfolio Concentration"
                          value={analytics.riskMetrics.concentration}
                          precision={2}
                          suffix="%"
                        />
                        <Progress
                          percent={analytics.riskMetrics.concentration}
                          status={analytics.riskMetrics.concentration > 30 ? 'exception' : 'success'}
                          showInfo={false}
                        />
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card>
                        <Statistic
                          title="Top 10 Holdings"
                          value={analytics.riskMetrics.top10Holdings}
                          precision={2}
                          suffix="%"
                        />
                        <Progress
                          percent={analytics.riskMetrics.top10Holdings}
                          status={analytics.riskMetrics.top10Holdings > 70 ? 'exception' : 'success'}
                          showInfo={false}
                        />
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </motion.div>
            </Col>

            <Col span={24}>
              <motion.div variants={fadeInUp}>
                <Card title={
                  <span>
                    <TeamOutlined style={{ marginRight: 8, color: '#13c2c2' }} />
                    Peer Analysis
                  </span>
                }>
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Card>
                        <Statistic
                          title="Portfolio Age"
                          value={analytics.peerAnalysis.portfolioAge}
                          suffix="months"
                        />
                        <Progress
                          percent={analytics.peerAnalysis.portfolioAge / 12 * 100}
                          showInfo={false}
                        />
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card>
                        <Statistic
                          title="Portfolio Size vs Income"
                          value={analytics.peerAnalysis.portfolioToIncome}
                          precision={2}
                          suffix="%"
                        />
                        <Progress
                          percent={analytics.peerAnalysis.portfolioToIncome}
                          status={analytics.peerAnalysis.portfolioToIncome > 100 ? 'exception' : 'success'}
                          showInfo={false}
                        />
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card>
                        <Statistic
                          title="Peer Ranking"
                          value={analytics.peerAnalysis.ranking}
                          suffix={`/ ${analytics.peerAnalysis.totalUsers}`}
                        />
                        <Progress
                          percent={(analytics.peerAnalysis.ranking / analytics.peerAnalysis.totalUsers) * 100}
                          status="active"
                          showInfo={false}
                        />
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </motion.div>
            </Col>

            <Col span={24}>
              <motion.div variants={fadeInUp}>
                <Card title={
                  <span>
                    <DollarOutlined style={{ marginRight: 8, color: '#eb2f96' }} />
                    Tax Analysis
                  </span>
                }>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Card title="Profit/Loss Breakdown">
                        <Row gutter={[16, 16]}>
                          <Col span={12}>
                            <Statistic
                              title="Short Term"
                              value={analytics.taxAnalysis.shortTermGains}
                              precision={2}
                              prefix="₹"
                              valueStyle={{ color: '#1890ff' }}
                            />
                          </Col>
                          <Col span={12}>
                            <Statistic
                              title="Long Term"
                              value={analytics.taxAnalysis.longTermGains}
                              precision={2}
                              prefix="₹"
                              valueStyle={{ color: '#52c41a' }}
                            />
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card title="Estimated Tax Liability">
                        <Statistic
                          title="Total Tax"
                          value={analytics.taxAnalysis.taxLiability}
                          precision={2}
                          prefix="₹"
                          valueStyle={{ color: '#f5222d', fontSize: '24px' }}
                        />
                        <Progress
                          percent={analytics.taxAnalysis.taxLiability / analytics.performanceMetrics.totalProfitLoss * 100}
                          status="exception"
                          showInfo={false}
                        />
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </TabPane>
      </Tabs>
    </motion.div>
  );
};

export default AnalyticsContainer;

// Add these styles to your CSS file
const styles = `
.analytics-tabs .ant-tabs-nav::before {
  border-bottom: 2px solid #f0f0f0;
}

.analytics-tabs .ant-tabs-tab {
  transition: all 0.3s;
}

.analytics-tabs .ant-tabs-tab:hover {
  color: #1890ff;
}

.analytics-tabs .ant-tabs-tab.ant-tabs-tab-active {
  transform: translateY(-2px);
}

.performance-card {
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.contributors-card, .detractors-card {
  transition: all 0.3s;
}

.contributors-card:hover, .detractors-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.contributors-table .ant-table-row {
  transition: all 0.3s;
}

.contributors-table .ant-table-row:hover {
  background-color: #f5f5f5;
}

.detractors-table .ant-table-row {
  transition: all 0.3s;
}

.detractors-table .ant-table-row:hover {
  background-color: #fff1f0;
}
`; 